/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, type ReactElement, useState } from 'react';
import { Form } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FormSubmitButton } from '@/components/buttons/form-submit-button';
import { type IdentifierError, validateIdentifier } from '@/extensions/priceshoes/lib/auth/identifier';

interface IdentificationFormProps {
    /** Error resolved by the login action (invalid identifier, customer not found or service error). */
    error?: string;
    returnUrl?: string | null;
}

/**
 * "Ingresa o activa tu cuenta" screen (PRIC-165): the shopper enters a partner ID or email and the
 * login action identifies it in Auth Extension. Includes the "Afíliate como soci@" entry point.
 */
export default function IdentificationForm({ error, returnUrl }: IdentificationFormProps): ReactElement {
    const { t } = useTranslation('extPriceshoes');
    const config = useConfig();
    const affiliationUrl = config.extension?.priceshoes?.affiliationUrl ?? '';
    const [identifierError, setIdentifierError] = useState<IdentifierError | null>(null);

    // Validate before submitting so format errors don't need a round trip; the action validates again.
    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        const identifier = new FormData(event.currentTarget).get('identifier')?.toString() ?? '';
        const validationError = validateIdentifier(identifier);
        setIdentifierError(validationError);
        if (validationError) {
            event.preventDefault();
        }
    };

    const fieldError = identifierError ? t(`login.errors.${identifierError}`) : error;

    return (
        <div className="space-y-6">
            <div className="space-y-4">
                <h1 className="text-2xl font-semibold text-foreground">{t('login.title')}</h1>
                <p className="text-foreground">{t('login.description')}</p>
            </div>

            <Form method="post" className="space-y-6" noValidate onSubmit={handleSubmit}>
                <div className="space-y-2">
                    <Label htmlFor="identifier">{t('login.identifierLabel')}</Label>
                    <Input
                        id="identifier"
                        name="identifier"
                        type="text"
                        autoComplete="username"
                        aria-invalid={fieldError ? true : undefined}
                        aria-describedby={fieldError ? 'identifier-error' : undefined}
                        onChange={() => setIdentifierError(null)}
                    />
                    {fieldError ? (
                        <p id="identifier-error" role="alert" className="text-sm text-destructive">
                            {fieldError}
                        </p>
                    ) : null}
                </div>

                <input type="hidden" name="loginMode" value="identify" />
                {returnUrl ? <input type="hidden" name="returnUrl" value={returnUrl} /> : null}

                <FormSubmitButton defaultText={t('login.next')} submittingText={t('login.submitting')} />
            </Form>

            <div className="flex items-center gap-4">
                <span className="h-px flex-1 bg-border" />
                <span className="text-foreground">{t('login.newCustomer')}</span>
                <span className="h-px flex-1 bg-border" />
            </div>

            {affiliationUrl ? (
                <Button asChild variant="outline" className="w-full">
                    <a href={affiliationUrl}>{t('login.affiliate')}</a>
                </Button>
            ) : (
                <Button type="button" variant="outline" className="w-full" disabled>
                    {t('login.affiliate')}
                </Button>
            )}
        </div>
    );
}
